<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <v-row class="d-flex align-center">
        <v-col>
          <v-tabs v-model="activeTab" background-color="transparent">
            <v-tab>
              <span>
                <v-icon left color="rgb(255, 65, 54)">mdi-play-circle</v-icon>
                Incidents
              </span>
            </v-tab>
            <v-tab>
              <span>
                <v-icon left color="#0084FF">mdi-play-circle</v-icon>
                Monitor Smart Facilities
              </span>
            </v-tab>
          </v-tabs>
        </v-col>

        <!-- Add Filter Controls to the right of tabs -->
        <v-col cols="auto" v-if="showFilters && activeTab === 0">
          <div class="d-flex align-center gap-2">
            <DateTimeFilter
              v-model="internalDateRange"
              :ranges="['Today', '2d', '1w', '1m']"
              default="1m"
              custom
              class="black-border d-flex align-center mt-1"
              :time-zone="facilityTimeZone"
            />
            <div class="ml-2">
              <IncidentFilter
                :showTypeFilter="true"
                :showStatusFilter="false"
                :showDispositionFilter="true"
                :showAccessCodeFilter="false"
                :showLocationFilter="false"
                :showSeverityFilter="true"
                :showOverdueFilter="true"
                @apply="applyVisualVerifyFilter"
              />
            </div>
          </div>
        </v-col>
      </v-row>
    </v-card-title>

    <v-card-text>
      <v-tabs-items v-model="activeTab">
        <v-tab-item>
          <IncidentsGrid
            ref="incidentsGrid"
            :items-per-page="itemsPerPageProp"
            :display-mode="displayMode"
            :show-location="showLocation"
            :external-date-range="effectiveDateRange"
            :incident-filter="internalIncidentFilter"
            :hide-date-filter="effectiveHideDateFilter"
            @open-video="handleOpenVideo"
          />
        </v-tab-item>

        <v-tab-item>
          <VideoGrid ref="videoGrid" />
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
  </v-card>
</template>

<script>
import IncidentsGrid from "./VideoGridIncidents.vue";
import VideoGrid from "./VideoGridLive.vue";
import DateTimeFilter from "@tod-ui/components/DateTimeFilter.vue";
import IncidentFilter from "../reports/IncidentFilter.vue";
import useFacility from "../authentication/useFacility";

export default {
  name: "VisualVerifyGrid",
  components: {
    IncidentsGrid,
    VideoGrid,
    DateTimeFilter,
    IncidentFilter,
  },
  props: {
    itemsPerPageProp: {
      type: Number,
      default: 6,
    },
    displayMode: {
      type: String,
      default: "detailed",
      validator: (value) => ["detailed", "compact"].includes(value),
    },
    showLocation: {
      type: Boolean,
      default: false,
    },
    // External props (when used from parent pages)
    externalDateRange: {
      type: Object,
      default: null,
    },
    incidentFilter: {
      type: Object,
      default: () => ({}),
    },
    hideDateFilter: {
      type: Boolean,
      default: false,
    },
    // New prop to control filter visibility
    showFilters: {
      type: Boolean,
      default: true, // Show filters by default
    },
  },
  setup() {
    const { facilityTimeZone } = useFacility();
    return { facilityTimeZone };
  },
  data() {
    return {
      activeTab: 0,
      // Internal state for filters when not controlled externally
      internalDateRange: {
        after: new Date(
          new Date().setMonth(new Date().getMonth() - 1)
        ).toISOString(),
        before: new Date().toISOString(),
      },
      internalIncidentFilter: {},
    };
  },
  computed: {
    // Use external date range if provided, otherwise use internal
    effectiveDateRange() {
      return this.externalDateRange || this.internalDateRange;
    },

    // Use external incident filter if provided, otherwise use internal
    effectiveIncidentFilter() {
      return Object.keys(this.incidentFilter).length > 0
        ? this.incidentFilter
        : this.internalIncidentFilter;
    },

    // Hide date filter if external date range is provided or if explicitly hidden
    effectiveHideDateFilter() {
      return (
        this.hideDateFilter || !!this.externalDateRange || this.showFilters
      );
    },
  },
  watch: {
    activeTab(newValue) {
      this.onTabChange(newValue);
    },

    // Watch external props and update internal state
    externalDateRange: {
      immediate: true,
      handler(newRange) {
        if (newRange) {
          this.internalDateRange = { ...newRange };
        }
      },
      deep: true,
    },

    incidentFilter: {
      immediate: true,
      handler(newFilter) {
        if (newFilter && Object.keys(newFilter).length > 0) {
          this.internalIncidentFilter = { ...newFilter };
        }
      },
      deep: true,
    },
  },
  methods: {
    handleOpenVideo(thumbnail) {
      if (this.$refs.videoGrid) {
        this.$refs.videoGrid.openVideoDialog({
          externalId: thumbnail.actorId,
        });
      }
    },

    onTabChange(tabIndex) {
      this.$emit("tab-changed", tabIndex);
    },

    applyVisualVerifyFilter(filter) {
      const _filter = { ...filter };

      // Transform filter data to match API expectations
      _filter.organizationSubtypes = _filter.locations;
      delete _filter.locations;

      if (_filter.severityStatus?.length) {
        _filter.severity = _filter.severityStatus;
      }
      delete _filter.severityStatus;

      if (_filter.overdueStatus?.length) {
        _filter.isOverdue = _filter.overdueStatus.includes("overdue");
      }
      delete _filter.overdueStatus;

      if (_filter.statuses?.length) {
        _filter.resolutions = [];

        if (_filter.statuses.includes("authorized")) {
          _filter.resolutions.push("claimed");
        }

        if (_filter.statuses.includes("unauthorized")) {
          _filter.resolutions.push("denied");
          _filter.resolutions.push("surveyed");
        }

        if (_filter.statuses.includes("unacknowledged")) {
          _filter.resolutions.push("unexplained");
        }
      }
      delete _filter.statuses;

      // Clean up null/empty values
      for (let k in _filter) {
        if (_filter[k] === null) {
          delete _filter[k];
        } else if (typeof _filter[k] === "boolean" && !_filter[k]) {
          delete _filter[k];
        } else if (Array.isArray(_filter[k]) && !_filter[k].length) {
          delete _filter[k];
        }
      }

      if (_filter.afterHours) {
        _filter["duringAccessHours"] =
          _filter.afterHours === "after-hours" ? false : undefined;
      }
      delete _filter.afterHours;

      // Update internal filter
      this.internalIncidentFilter = _filter;

      // Trigger refresh of incidents grid
      this.$nextTick(() => {
        if (this.$refs.incidentsGrid) {
          this.$refs.incidentsGrid.fetchIncidents();
        }
      });

      // Emit filter change for parent components
      this.$emit("filter-applied", _filter);
    },

    // Public method to refresh incidents (can be called by parent)
    fetchIncidents() {
      if (this.$refs.incidentsGrid) {
        this.$refs.incidentsGrid.fetchIncidents();
      }
    },
  },
};
</script>
